import { Injectable } from '@angular/core';
import { Environment } from 'src/environments/environment';
import { GlobalConstService } from './global-const.service';
import { HttpService } from './http.service';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { FileService } from './file.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { PlatformService } from './platform.service';
import { PromptCameraService } from './prompt-camera.service';
import { DocumentPickerCapacitorService } from './document-picker-capacitor.service';

@Injectable({ providedIn: 'root' })
export class SurveyService {
  public surveyInitConfig;

  public images$: BehaviorSubject<any> = new BehaviorSubject([]);
  public documents$: BehaviorSubject<any> = new BehaviorSubject([]);

  constructor(
    private httpService: HttpService,
    private fileService: FileService,
    private httpClient: HttpClient,
    private platformService: PlatformService,
    private promptCameraService: PromptCameraService,
    private documentPickerCapacitor: DocumentPickerCapacitorService
  ) {}

  public getSurvey(idPage: number) {
    return this.httpService.myGet(`${Environment.API_BASE_URL_V2}/api/empatica/v1/components/form/${idPage}/elements`);
  }

  public sendSurvey(idPage: number, formResponses): Observable<any> {
    const formData = new FormData();

    formResponses.forEach((response, index) => {
      if (response.answers && response.answers.length > 0) {
        response.answers.forEach((answer, answerIndex) => {
          
          if (answer && answer.blob instanceof Blob) {
            console.log(`Adding blob to FormData for response ${response.id}`);
            console.log(`Blob details - size: ${answer.blob.size}, type: ${answer.blob.type}`);
            const fieldName = `${response.id}`;
            formData.append(fieldName, answer.blob, answer.name);
            console.log(`Blob added to FormData with name: ${answer.name}`);
          } else if (answer !== null && typeof answer === 'object' && answer.blob && answer.blob instanceof Blob) {
            console.log(`Processing object answer with blob for response ${response.id}`);
            const fieldName = `${response.id}`;
            formData.append(fieldName, answer.blob, answer.name || `response_${response.id}`);
            console.log(`Object blob added to FormData with name: ${answer.name || `response_${response.id}`}`);
          } else if (answer !== null && answer instanceof Blob) {
            const fieldName = `${response.id}`;
            formData.append(fieldName, answer, `response_${response.id}`);
          } else if (answer !== null && typeof answer === 'object') {
            console.log(`Processing object answer for response ${response.id}`);
            Object.keys(answer).forEach((key) => {
              const value = answer[key];
              const fieldName = `${response.id}.${key}`;

              if (value instanceof File) {
                formData.append(fieldName, value, value.name);
              } else {
                formData.append(fieldName, JSON.stringify(value));
              }
            });
          } else if (answer !== null && answer !== undefined) {
            console.log(`Adding non-blob answer to FormData for response ${response.id}:`, answer);
            const fieldName = `${response.id}`;
            formData.append(fieldName, answer);
          } else {
            console.log(`Skipping null/undefined answer for response ${response.id}`);
          }
        });
      } else {
        // Si no hay respuestas para esta pregunta, no agregar nada al FormData
        console.log(`No answers for response ${response.id}`);
      }
    });

    console.log('FormData created successfully');

    return this.httpService.myPost(`${Environment.API_BASE_URL_V2}/api/empatica/v1/components/form/${idPage}/submit`, formData);
  }

  public async prepareUserResponse(values, surveyFormDataResponse) {
    const listOfResponses = [];

    for (const question of surveyFormDataResponse) {
      const response = await this.setUserResponse(values, question);
      
      listOfResponses.push({
        id: question.id,
        answers: response
      });
    }
    
    return listOfResponses;
  }

  private async setUserResponse(values, question) {
    const keysResponse = Object.keys(values);
    const responseKey = keysResponse.find((res) => res === question.question);

    if (question.inputType === 'image') {
      return this.processImageResponse(values, question, responseKey);
    } else {
      return values[responseKey] !== undefined && values[responseKey] !== null 
        ? [values[responseKey]] 
        : [];
    }
  }

  private async processImageResponse(values, question, responseKey) {
    const images = this.images$.value;
    const documents = this.documents$.value;
    
    const image = images.find((img) => img.questionId === question.id);
    const document = documents.find((doc) => doc.questionId === question.id);

    // Prioridad 1: Blob directo del formulario (web)
    if (values[responseKey] instanceof Blob) {
      return [{
        blob: values[responseKey],
        name: `response_${question.id}`,
        image: null,
        document: null
      }];
    }
    
    // Prioridad 2: Blob del servicio (nativo)
    if (image?.image?.blob) {
      return [{
        blob: image.image.blob,
        name: `response_${question.id}`,
        image: image.image,
        document: null
      }];
    }

    // Prioridad 3: Documento del servicio
    if (document?.document?.blob) {
      return [{
        blob: document.document.blob,
        name: `response_${question.id}`,
        image: null,
        document: document.document
      }];
    }

    // Si no hay datos y el campo no es obligatorio
    if (!question.mandatory && !image && !document) {
      return [];
    }

    // Si no hay datos y el campo es obligatorio
    if (question.mandatory && !image && !document) {
      return [];
    }

    // Procesar imagen si existe (fallback para casos edge)
    if (image) {
      const blob = await this.createBlobFromImage(image);
      if (blob) {
        return [{
          blob: blob,
          name: `response_${question.id}`,
          image: image.image,
          document: null
        }];
      }
    }

    return [];
  }

  private async createBlobFromImage(image) {
    try {
      if (this.platformService.isWeb()) {
        return this.createBlobForWeb(image);
      } else {
        return this.createBlobForNative(image);
      }
    } catch (error) {
      console.error('Error creating blob from image:', error);
      return null;
    }
  }

  private createBlobForWeb(image) {
    if (image.image?.webPath && typeof image.image.webPath === 'string' && image.image.webPath.startsWith('data:')) {
      return this.fileService.base64ToBlob(
        image.image.webPath, 
        `data:image/${image.image.format || 'jpeg'}`
      );
    } else if (image.image?.blob) {
      return image.image.blob;
    } else if (image.image?.webPath && typeof image.image.webPath === 'string') {
      return this.httpClient.get(image.image.webPath, { responseType: 'blob' }).toPromise();
    }
    return null;
  }

  private async createBlobForNative(image) {
    if (image.image?.blob) {
      return image.image.blob;
    } else if (image.image?.path || image.image?.webPath) {
      const base64 = await this.fileService.convertFilePathToBase64(
        image.image.path || image.image.webPath
      );
      if (typeof base64 === 'string') {
        return this.base64ToBlobPure(base64, `image/${image.image.format || 'jpeg'}`);
      }
    }
    return null;
  }

  public async uploadCameraPictureWeb(event, form, question, fileImage) {
    if (event.target.files && event.target.files.length) {
      let [file] = event.target.files;
      file = await this.fileService.generateFile(file);

      if (form) {
        form.patchValue({ [question.question]: file });
        // Marcar el control como touched y dirty para que la validación funcione correctamente
        const control = form.get(question.question);
        if (control) {
          control.markAsTouched();
          control.markAsDirty();
        }
      }

      this.uploadPicture(file, question.id); // Associate the image with the question ID
    }
    fileImage.nativeElement.value = '';
  }

  public async selectPicture(fileInput, question, form?) {
    // En web, usar directamente el input file
    if (this.platformService.isWeb()) {
      console.log('Web platform detected, using direct file input for pictures');
      fileInput.nativeElement.accept = 'image/*';
      fileInput.nativeElement.click();
      return;
    }
    
    // Para móviles, usar PromptCameraService para manejar cámara/galería
    console.log('Mobile platform detected, using PromptCameraService');
    try {
      const result = await this.promptCameraService.ask();
      console.log('PromptCameraService result:', result);
      
      switch (result.action) {
        case this.promptCameraService.OPEN_INPUT_FILE:
          console.log('Opening file input as fallback');
          fileInput.nativeElement.click();
          break;
        case this.promptCameraService.REMOVE_IMAGE:
          console.log('Remove image selected');
          break;
        default:
          if (result.image) {
            console.log('Image selected from camera/gallery:', result.image);
            // Procesar la imagen seleccionada
            await this.uploadPicture(result.image, question.id, form, question);
            
            // Disparar un evento personalizado para notificar al componente
            const customEvent = new CustomEvent('imageUploaded', {
              detail: { questionId: question.id }
            });
            document.dispatchEvent(customEvent);
          }
          break;
      }
    } catch (error) {
      console.error('Error with PromptCameraService:', error);
      // Fallback: usar directamente el input file
      console.log('Using fallback file input');
      fileInput.nativeElement.click();
    }
  }

  public async uploadPicture(image, questionId, form?: any, question?: any) {
    console.log('uploadPicture called with:', { image, questionId, hasForm: !!form });
    console.log('Platform isWeb:', this.platformService.isWeb());
    console.log('Platform isMobile:', this.platformService.isMobile());
    
    const currentImages = this.images$.value;
    console.log('Current images:', currentImages);
    
    // Remover imagen anterior si existe para esta pregunta
    const filteredImages = currentImages.filter((img) => img.questionId !== questionId);
    console.log('Filtered images:', filteredImages);
    
    // En web, el objeto image puede ser un processedFile con blob e image
    let newImage;
    if (this.platformService.isWeb() && image && image.blob && image.image) {
      console.log('Processing as web processedFile');
      // Es un processedFile de web
      newImage = { 
        questionId, 
        image: {
          webPath: image.image,
          path: image.image,
          format: image.name ? image.name.split('.').pop() : 'jpeg',
          name: image.name,
          blob: image.blob // Asegurar que el blob se incluya
        }
      };
    } else {
      console.log('Processing as native image');
      // Es una imagen normal (móvil) o un objeto nativo
      // Si es un objeto nativo de Capacitor, ya tiene la estructura correcta
      if (image && (image.path || image.webPath)) {
        console.log('Detected native Capacitor object');
        // Es un objeto nativo de Capacitor (cámara/galería)
        // Crear el blob inmediatamente para nativo
        try {
          const base64: any = await this.fileService.convertFilePathToBase64(image.path || image.webPath);
          console.log('Base64 converted, creating blob');
          // Crear el blob directamente desde el base64 puro
          const blob = this.base64ToBlobPure(base64, `image/${image.format || 'jpeg'}`);
          
          newImage = { 
            questionId, 
            image: {
              ...image,
              blob: blob // Agregar el blob al objeto de imagen
            }
          };
          console.log('Native image processed with blob');
          
          // Si tenemos un formulario, actualizar el campo con el blob
          if (form && newImage.image.blob && question) {
            console.log('Updating form field with blob');
            const control = form.get(question.question);
            if (control) {
              control.setValue(newImage.image.blob);
              control.markAsTouched();
              control.markAsDirty();
              console.log('Form field updated with blob');
            }
          }
        } catch (error) {
          console.error('Error creating blob for native image:', error);
          // Fallback: guardar sin blob
          newImage = { questionId, image };
        }
      } else {
        console.log('Using fallback image object');
        // Es una imagen normal (fallback)
        newImage = { questionId, image };
      }
    }
    
    console.log('New image object:', newImage);
    
    const updatedImages = [...filteredImages, newImage]; // Create a new array with the new item
    console.log('Updated images array:', updatedImages);
    
    this.images$.next(updatedImages);
    console.log('Images$ updated successfully');

    // Limpiar documento si existe para esta pregunta
    const currentDocuments = this.documents$.value;
    const filteredDocuments = currentDocuments.filter((doc) => doc.questionId !== questionId);
    this.documents$.next(filteredDocuments);
    console.log('Documents$ cleaned for question:', questionId);
  }

  public async uploadDocumentWeb(event, form, question, fileDocument) {
    if (event.target.files && event.target.files.length) {
      let [file] = event.target.files;
      file = await this.fileService.generateDocumentFile(file);

      if (form) {
        form.patchValue({ [question.question]: file });
        // Marcar el control como touched y dirty para que la validación funcione correctamente
        const control = form.get(question.question);
        if (control) {
          control.markAsTouched();
          control.markAsDirty();
        }
      }

      this.uploadDocument(file, question.id, form, question); // Associate the document with the question ID
    }
    fileDocument.nativeElement.value = '';
  }

  public async selectDocument(fileInput, question, form?) {
    console.log('SurveyService.selectDocument() called');
    
    // En web, mostrar un action sheet simple antes de ir al selector de archivos
    if (this.platformService.isWeb()) {
      console.log('Web platform detected, showing action sheet');
      
      // Crear un action sheet simple para web
      const actionSheet = document.createElement('div');
      actionSheet.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.5);
        z-index: 9999;
        display: flex;
        align-items: flex-end;
      `;
      
      const content = document.createElement('div');
      content.style.cssText = `
        background: white;
        width: 100%;
        border-radius: 16px 16px 0 0;
        padding: 20px;
        box-shadow: 0 -4px 20px rgba(0,0,0,0.2);
      `;
      
      const options = [
        { text: 'Seleccionar imagen', action: 'select_image' },
        { text: 'Seleccionar documento', action: 'select_document' },
        { text: 'Cancelar', action: 'cancel' }
      ];
      
      options.forEach(option => {
        const button = document.createElement('button');
        button.textContent = option.text;
        button.style.cssText = `
          width: 100%;
          padding: 16px;
          margin: 8px 0;
          border: none;
          background: ${option.action === 'cancel' ? '#f0f0f0' : 'white'};
          color: ${option.action === 'cancel' ? '#666' : '#000'};
          border-radius: 8px;
          font-size: 16px;
          cursor: pointer;
        `;
        
        button.onclick = async () => {
          document.body.removeChild(actionSheet);
          
          if (option.action === 'select_image') {
            // Configurar el input para imágenes
            fileInput.nativeElement.accept = 'image/*';
            
            // Agregar listener temporal para procesar la imagen
            const handleFileChange = async (event) => {
              const file = event.target.files[0];
              if (file) {
                console.log('Image selected in web:', file);
                // Procesar la imagen como si fuera una foto
                try {
                  // Procesar el archivo para que sea compatible con el sistema
                  const processedFile = await this.fileService.generateFile(file);
                  console.log('Processed file:', processedFile);
                  await this.uploadPicture(processedFile, question.id);
                  
                  // Disparar un evento personalizado para notificar al componente
                  const customEvent = new CustomEvent('imageUploaded', {
                    detail: { questionId: question.id }
                  });
                  document.dispatchEvent(customEvent);
                  
                } catch (error) {
                  console.error('Error processing image:', error);
                  // Fallback: usar el archivo original
                  await this.uploadPicture(file, question.id);
                  
                  // Disparar evento incluso en caso de error
                  const customEvent = new CustomEvent('imageUploaded', {
                    detail: { questionId: question.id }
                  });
                  document.dispatchEvent(customEvent);
                }
                // Limpiar el listener
                fileInput.nativeElement.removeEventListener('change', handleFileChange);
              }
            };
            
            fileInput.nativeElement.addEventListener('change', handleFileChange);
            fileInput.nativeElement.click();
          } else if (option.action === 'select_document') {
            // Configurar el input para documentos
            fileInput.nativeElement.accept = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ods,.odp,.xls,.xlsx,.ppt,.pptx';
            
            // Agregar listener temporal para procesar el documento
            const handleFileChange = async (event) => {
              const file = event.target.files[0];
              if (file) {
                console.log('Document selected in web:', file);
                try {
                  // Procesar el documento
                  const processedFile = await this.fileService.generateDocumentFile(file);
                  console.log('Processed document:', processedFile);
                  await this.uploadDocument(processedFile, question.id, form, question);
                  
                  // Disparar un evento personalizado para notificar al componente
                  const customEvent = new CustomEvent('documentUploaded', {
                    detail: { questionId: question.id }
                  });
                  document.dispatchEvent(customEvent);
                  
                } catch (error) {
                  console.error('Error processing document:', error);
                  // Fallback: usar el archivo original
                  await this.uploadDocument(file, question.id, form, question);
                  
                  // Disparar evento incluso en caso de error
                  const customEvent = new CustomEvent('documentUploaded', {
                    detail: { questionId: question.id }
                  });
                  document.dispatchEvent(customEvent);
                }
                // Limpiar el listener
                fileInput.nativeElement.removeEventListener('change', handleFileChange);
              }
            };
            
            fileInput.nativeElement.addEventListener('change', handleFileChange);
            fileInput.nativeElement.click();
          }
        };
        
        content.appendChild(button);
      });
      
      actionSheet.appendChild(content);
      document.body.appendChild(actionSheet);
      
      // Cerrar al hacer clic fuera del action sheet
      actionSheet.onclick = (e) => {
        if (e.target === actionSheet) {
          document.body.removeChild(actionSheet);
        }
      };
      
      return;
    }
    
    // Para móviles, usar PromptCameraService para mostrar el action sheet
    console.log('Mobile platform detected, using PromptCameraService');
    try {
      const result = await this.promptCameraService.show(false, true);
      console.log('PromptCameraService result:', result);
      
      switch (result.action) {
        case 'take_photo':
        case 'select_photo':
          // Se seleccionó una imagen
          console.log('Image action detected:', result.action);
          if (result.image) {
            console.log('Calling uploadPicture with result.image:', result.image);
            await this.uploadPicture(result.image, question.id, form, question);
            console.log('uploadPicture completed');
            
            // Disparar un evento personalizado para notificar al componente
            const customEvent = new CustomEvent('imageUploaded', {
              detail: { questionId: question.id }
            });
            document.dispatchEvent(customEvent);
          } else {
            console.log('No image in result');
          }
          break;
        case this.promptCameraService.OPEN_INPUT_FILE:
          // Se seleccionó documento, usar DocumentPickerCapacitorService
          console.log('Document selection via OPEN_INPUT_FILE');
          try {
            console.log('Using DocumentPickerCapacitorService for document selection');
            const documentResult = await this.documentPickerCapacitor.pickDocument();
            
            if (documentResult && documentResult.success && documentResult.document) {
              console.log('Document selected via DocumentPickerCapacitorService:', documentResult.document);
              await this.uploadDocument(documentResult.document, question.id, form, question);
              
              // Disparar un evento personalizado para notificar al componente
              const customEvent = new CustomEvent('documentUploaded', {
                detail: { questionId: question.id }
              });
              document.dispatchEvent(customEvent);
            }
          } catch (error) {
            console.error('Error with DocumentPickerCapacitorService:', error);
            // Fallback: usar input file nativo
            fileInput.nativeElement.accept = '.pdf,.doc,.docx,.txt,.rtf,.odt,.ods,.odp,.xls,.xlsx,.ppt,.pptx';
            
            // Agregar listener temporal para procesar el documento
            const handleFileChange = async (event) => {
              const file = event.target.files[0];
              if (file) {
                console.log('Document selected in mobile (fallback):', file);
                try {
                  // Procesar el documento
                  const processedFile = await this.fileService.generateDocumentFile(file);
                  console.log('Processed document:', processedFile);
                  await this.uploadDocument(processedFile, question.id, form, question);
                } catch (error) {
                  console.error('Error processing document:', error);
                  // Fallback: usar el archivo original
                  await this.uploadDocument(file, question.id, form, question);
                }
                // Limpiar el listener
                fileInput.nativeElement.removeEventListener('change', handleFileChange);
              }
            };
            
            fileInput.nativeElement.addEventListener('change', handleFileChange);
            fileInput.nativeElement.click();
          }
          break;
        default:
          console.log('No action selected or action not handled');
          break;
      }
    } catch (error) {
      console.error('Error with PromptCameraService:', error);
      // Fallback: usar directamente el input file
      fileInput.nativeElement.click();
    }
  }

  public async selectPictureWithDocuments(fileImageInput, fileDocumentInput, question, form?) {
    // En web, ir directamente al input de documentos
    if (this.platformService.isWeb()) {
      console.log('Web platform detected, using direct file input for documents');
      fileDocumentInput.nativeElement.accept = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ods,.odp,*/*';
      fileDocumentInput.nativeElement.click();
      return;
    }
    
    // Para móviles, usar directamente el input file como fallback
    console.warn('Mobile platform detected, using fallback for documents');
    fileDocumentInput.nativeElement.click();
  }

  public async uploadDocument(document, questionId, form?: any, question?: any) {
    console.log('uploadDocument called with:', { document, questionId });
    console.log('Document structure:', {
      hasBlob: !!document?.blob,
      hasPath: !!document?.path,
      hasWebPath: !!document?.webPath,
      documentType: document?.type,
      documentName: document?.name
    });
    
    const currentDocuments = this.documents$.value;
    // Remover documento anterior si existe para esta pregunta
    const filteredDocuments = currentDocuments.filter((doc) => doc.questionId !== questionId);
    
    let newDocument;
    
    // Si el documento ya tiene blob (web o nativo), usarlo directamente
    if (document && document.blob) {
      console.log('Document already has blob (web or native)');
      newDocument = { questionId, document };
    } else if (document && (document.path || document.webPath)) {
      // Es un documento nativo, crear blob si no existe
      console.log('Processing native document without blob');
      try {
        if (!document.blob) {
          const base64: any = await this.fileService.convertFilePathToBase64(document.path || document.webPath);
          // Crear el blob directamente desde el base64 puro
          const blob = this.base64ToBlobPure(base64, document.type || 'application/octet-stream');
          document.blob = blob;
          console.log('Created blob for native document');
        }
        newDocument = { questionId, document };
        
        // Si tenemos un formulario, actualizar el campo con el blob
        if (form && document.blob && question) {
          console.log('Updating form field with document blob');
          const control = form.get(question.question);
          if (control) {
            control.setValue(document.blob);
            control.markAsTouched();
            control.markAsDirty();
            console.log('Form field updated with document blob');
          }
        }
      } catch (error) {
        console.error('Error creating blob for native document:', error);
        newDocument = { questionId, document };
      }
    } else {
      // Fallback
      console.log('Using fallback document object');
      newDocument = { questionId, document };
    }
    
    const updatedDocuments = [...filteredDocuments, newDocument];
    console.log('Updated documents array:', updatedDocuments);
    
    this.documents$.next(updatedDocuments);
    console.log('Documents$ updated successfully');

    // Limpiar imagen si existe para esta pregunta
    const currentImages = this.images$.value;
    const filteredImages = currentImages.filter((img) => img.questionId !== questionId);
    this.images$.next(filteredImages);
    console.log('Images$ cleaned for question:', questionId);
  }

  /**
   * Convierte un string base64 puro a un Blob
   * @param base64String - El string base64 sin el prefijo data:
   * @param contentType - El tipo MIME del contenido
   * @returns Un Blob con el contenido
   */
  private base64ToBlobPure(base64String: string, contentType: string): Blob {
    console.log('base64ToBlobPure called with:', { 
      base64StringLength: base64String.length,
      base64StringStart: base64String.substring(0, 50) + '...', 
      contentType 
    });
    
    try {
      const binaryString = atob(base64String);
      console.log('Binary string length:', binaryString.length);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: contentType });
      console.log('Blob created successfully, size:', blob.size, 'type:', blob.type);
      return blob;
    } catch (error) {
      console.error('Error creating blob from base64:', error);
      throw error;
    }
  }
}
